import React, { useState, useEffect, useMemo } from "react";
import {
  collection,
  onSnapshot,
  updateDoc,
  doc,
} from "firebase/firestore";
import { db } from "../config/firebase";
import PageTemplate from "../templates/PageTemplate";
import { useNavigate } from "react-router-dom";
import moment from "moment";

const ITEMS_PER_PAGE = parseInt(process.env.REACT_APP_ITEMS_PER_PAGE, 10) || 20;

// Fonction pour normaliser les chaînes (enlever les accents)
const normalizeString = (str) => {
  if (!str) return "";
  return str
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
};

export default function UsersAdmin() {
  const navigate = useNavigate();
  const [allUsers, setAllUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filterVerified, setFilterVerified] = useState("all");
  const [filterActive, setFilterActive] = useState("all");

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "users"), (querySnapshot) => {
      const items = [];
      querySnapshot.forEach((d) => items.push({ id: d.id, ...d.data() }));
      // Tri côté client pour inclure les docs sans createdAt
      items.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
        return dateB - dateA;
      });
      setAllUsers(items);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    let filtered = allUsers;

    // Filtre de recherche avec normalisation des accents
    const st = normalizeString(searchTerm);
    if (st) {
      filtered = filtered.filter(
        (u) =>
          normalizeString(u.firstname).includes(st) ||
          normalizeString(u.lastname).includes(st) ||
          normalizeString(u.email).includes(st) ||
          normalizeString(u.city).includes(st) ||
          normalizeString(u.department).includes(st)
      );
    }

    // Filtre vérifié - CORRECTION ICI: utiliser emailIsVerified
    if (filterVerified === "verified") {
      filtered = filtered.filter((u) => u.emailIsVerified);
    } else if (filterVerified === "notVerified") {
      filtered = filtered.filter((u) => !u.emailIsVerified);
    }

    // Filtre actif
    if (filterActive === "active") {
      filtered = filtered.filter((u) => u.isActive);
    } else if (filterActive === "inactive") {
      filtered = filtered.filter((u) => !u.isActive);
    }

    setFilteredUsers(filtered);
    setPage(1);
  }, [searchTerm, allUsers, filterVerified, filterActive]);

  const pageCount = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);
  const currentUsers = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredUsers.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredUsers, page]);

  const updateUserLocal = (id, changes) => {
    setAllUsers((old) =>
      old.map((u) => (u.id === id ? { ...u, ...changes } : u))
    );
    setFilteredUsers((old) =>
      old.map((u) => (u.id === id ? { ...u, ...changes } : u))
    );
  };

  const toggleActive = async (user) => {
    const userRef = doc(db, "users", user.id);
    await updateDoc(userRef, { isActive: !user.isActive });
    updateUserLocal(user.id, { isActive: !user.isActive });

    // Success notification
    const successDiv = document.createElement("div");
    successDiv.className =
      "fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2";
    successDiv.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
        <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
      </svg>
      Utilisateur ${!user.isActive ? "activé" : "désactivé"} avec succès !
    `;
    document.body.appendChild(successDiv);
    setTimeout(() => successDiv.remove(), 3000);
  };

  const toggleVerified = async (user) => {
    // CORRECTION ICI: utiliser emailIsVerified
    const userRef = doc(db, "users", user.id);
    await updateDoc(userRef, { emailIsVerified: !user.emailIsVerified });
    updateUserLocal(user.id, { emailIsVerified: !user.emailIsVerified });

    // Success notification
    const successDiv = document.createElement("div");
    successDiv.className =
      "fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2";
    successDiv.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
        <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
      </svg>
      Statut de vérification mis à jour !
    `;
    document.body.appendChild(successDiv);
    setTimeout(() => successDiv.remove(), 3000);
  };

  const formatTimestamp = (ts) =>
    ts && ts.toDate ? moment(ts.toDate()).format("DD/MM/YY HH:mm") : "-";

  // CORRECTION ICI: utiliser emailIsVerified
  const verifiedCount = allUsers.filter((u) => u.emailIsVerified).length;
  const activeCount = allUsers.filter((u) => u.isActive).length;

  return (
    <PageTemplate
      loading={loading}
      content={
        <div className="space-y-4">
          {/* Stats row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white rounded-lg border border-gray-200 p-3">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Total</p>
              <p className="text-lg font-semibold text-gray-900 mt-0.5">{allUsers.length}</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-3">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Vérifiés</p>
              <p className="text-lg font-semibold text-gray-900 mt-0.5">{verifiedCount}</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-3">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Actifs</p>
              <p className="text-lg font-semibold text-gray-900 mt-0.5">{activeCount}</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-3">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Affichés</p>
              <p className="text-lg font-semibold text-gray-900 mt-0.5">{filteredUsers.length}</p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3 bg-white rounded-lg border border-gray-200 p-2">
            <div className="relative flex-1 min-w-[200px]">
              <input
                type="text"
                placeholder="Rechercher par nom, email, ville..."
                className="w-full border border-gray-300 rounded-md px-3 py-1.5 pl-8 text-xs focus:outline-none focus:ring-1 focus:ring-gray-400"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoComplete="off"
              />
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-2">
                <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
              </svg>
            </div>
            <select
              value={filterVerified}
              onChange={(e) => setFilterVerified(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-gray-400"
            >
              <option value="all">Vérification: Tous</option>
              <option value="verified">Vérifiés</option>
              <option value="notVerified">Non vérifiés</option>
            </select>
            <select
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-gray-400"
            >
              <option value="all">Statut: Tous</option>
              <option value="active">Actifs</option>
              <option value="inactive">Inactifs</option>
            </select>
          </div>

          {/* Table */}
          <div className="bg-white rounded-lg border border-gray-200">
            {currentUsers.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-sm text-gray-500">Aucun utilisateur trouvé</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Profil</th>
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Nom</th>
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Téléphone</th>
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Ville</th>
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Créé le</th>
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {currentUsers.map((user) => (
                      <tr key={user.id} onClick={() => navigate(`/users/${user.id}`)} className="hover:bg-gray-50 cursor-pointer">
                        <td className="px-3 py-2">
                          {user.photo ? (
                            <img src={user.photo} alt="" className="w-8 h-8 rounded-full object-cover" />
                          ) : (
                            <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-gray-600 text-xs font-medium">
                              {user.firstname?.[0]?.toUpperCase() || "?"}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap">{user.firstname} {user.lastname}</td>
                        <td className="px-3 py-2 text-gray-600 text-xs">{user.email}</td>
                        <td className="px-3 py-2 text-gray-600 text-xs">{user.phoneNumber || "-"}</td>
                        <td className="px-3 py-2 text-gray-600 text-xs">{user.city || "-"}</td>
                        <td className="px-3 py-2">
                          <span className="text-xs bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded">{user.type || "-"}</span>
                        </td>
                        <td className="px-3 py-2 text-gray-500 whitespace-nowrap text-xs">{formatTimestamp(user.createdAt)}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1">
                            {user.emailIsVerified && <span className="text-xs bg-green-50 text-green-700 px-1.5 py-0.5 rounded">Vérifié</span>}
                            {user.isActive && <span className="text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">Actif</span>}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <button onClick={(e) => { e.stopPropagation(); toggleActive(user); }} className={`text-xs font-medium ${user.isActive ? "text-red-600 hover:text-red-800" : "text-green-600 hover:text-green-800"}`}>
                              {user.isActive ? "Désactiver" : "Activer"}
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); toggleVerified(user); }} className={`text-xs font-medium ${user.emailIsVerified ? "text-orange-600 hover:text-orange-800" : "text-green-600 hover:text-green-800"}`}>
                              {user.emailIsVerified ? "Retirer vérif." : "Vérifier"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {pageCount > 1 && (
              <div className="flex justify-center items-center py-3 border-t border-gray-200 gap-3">
                <button
                  onClick={() => setPage((p) => Math.max(p - 1, 1))}
                  disabled={page === 1}
                  className="px-3 py-1 rounded border border-gray-300 text-xs font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Précédent
                </button>
                <span className="text-xs text-gray-600">Page {page} / {pageCount}</span>
                <button
                  onClick={() => setPage((p) => Math.min(p + 1, pageCount))}
                  disabled={page === pageCount}
                  className="px-3 py-1 rounded border border-gray-300 text-xs font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Suivant
                </button>
              </div>
            )}
          </div>
        </div>
      }
    />
  );
}
